import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RetryIndicator from '../../../components/ui/RetryIndicator';
import { useRetryState } from '../../../hooks/useRetryState';
import authService from '../../../services/authService';
import { retryWithBackoff } from '../../../utils/retryWithBackoff';
import Icon from '../../../components/AppIcon';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { retryState, startRetry, updateRetry, completeRetry, resetRetry } = useRetryState();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email) newErrors.email = 'E-mailadres is verplicht';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) newErrors.email = 'Ongeldig e-mailadres';
    if (!formData?.password) newErrors.password = 'Wachtwoord is verplicht';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    startRetry();
    try {
      const { user } = await retryWithBackoff(() => authService?.signIn(formData?.email, formData?.password), {}, updateRetry);
      const profile = await retryWithBackoff(() => authService?.getUserProfile(user?.id), {}, updateRetry);
      completeRetry(true);
      if (profile?.role !== 'superadmin' && !profile?.profile_completed) {
        navigate('/profile-completion-wizard-step-1-role-confirmation');
        return;
      }
      const roleRoutes = { superadmin: '/super-admin-dashboard', client: '/client-job-management', contractor: '/contractor-dashboard', affiliate: '/affiliate-dashboard' };
      navigate(roleRoutes?.[profile?.role] || '/contractor-dashboard');
    } catch (error) {
      console.error('Login error:', error);
      completeRetry(false, error?.message);
      setErrors({ email: error?.message || 'Onjuiste inloggegevens. Controleer uw e-mailadres en wachtwoord.', password: ' ' });
    } finally {
      setIsLoading(false);
      setTimeout(resetRetry, 3000);
    }
  };

  const inputStyle = {
    background: '#141414',
    border: '1px solid #2A2A2A',
    borderRadius: '2px',
    color: '#FFFFFF',
    width: '100%',
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: 'DM Sans, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s'
  };

  return (
    <div className="w-full">
      <h2 className="text-2xl font-heading font-bold text-white mb-6">Inloggen</h2>
      <form onSubmit={handleSubmit} className="space-y-5">
        <RetryIndicator
          isRetrying={retryState?.isRetrying}
          attempt={retryState?.attempt}
          maxRetries={retryState?.maxRetries}
          delay={retryState?.delay}
          error={retryState?.error}
          success={retryState?.success}
          className="mb-4"
        />
        
        {/* Email */}
        <div>
          <label className="block text-xs font-body font-semibold tracking-wide uppercase mb-2" style={{ color: '#555555' }}>E-mailadres</label>
          <input
            type="email"
            placeholder="uw.email@voorbeeld.nl"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            style={inputStyle}
            onFocus={e => e.target.style.borderColor = '#00E5A0'}
            onBlur={e => e.target.style.borderColor = errors?.email ? '#EF4444' : '#2A2A2A'}
            required
          />
          {errors?.email && <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors?.email}</p>}
        </div>

        {/* Password */}
        <div>
          <label className="block text-xs font-body font-semibold tracking-wide uppercase mb-2" style={{ color: '#555555' }}>Wachtwoord</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Voer uw wachtwoord in"
              value={formData?.password}
              onChange={(e) => handleInputChange('password', e?.target?.value)}
              style={{ ...inputStyle, paddingRight: '44px' }}
              onFocus={e => e.target.style.borderColor = '#00E5A0'}
              onBlur={e => e.target.style.borderColor = errors?.password ? '#EF4444' : '#2A2A2A'}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: '#555555' }}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
            </button>
          </div>
          {errors?.password && errors?.password !== ' ' && <p className="text-xs mt-1" style={{ color: '#EF4444' }}>{errors?.password}</p>}
        </div>

        {/* Remember me + forgot */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData?.rememberMe}
              onChange={(e) => handleInputChange('rememberMe', e?.target?.checked)}
              className="w-4 h-4 rounded-sm"
              style={{ accentColor: '#00E5A0' }}
            />
            <span className="text-xs font-body" style={{ color: '#888888' }}>Onthouden</span>
          </label>
          <button type="button" className="text-xs font-body transition-colors hover:text-white" style={{ color: '#00E5A0' }}>
            Wachtwoord vergeten?
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 text-sm font-body font-semibold flex items-center justify-center gap-2 transition-all duration-200"
          style={{ background: isLoading ? '#1A1A1A' : '#00E5A0', color: isLoading ? '#555555' : '#0A0A0A', borderRadius: '2px' }}
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 rounded-full border-2 animate-spin" style={{ borderColor: '#333', borderTopColor: '#00E5A0' }} />
              Inloggen...
            </>
          ) : (
            <>
              Inloggen
              <Icon name="LogIn" size={16} color="#0A0A0A" />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default LoginForm;